// Schedule + triggers for a batch pipeline: cron / time zone / enabled on the
// pipeline doc (`schedule`), the worker's `scheduleState` read back (next
// fire, last job), and the other ways this pipeline gets run.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const PRESETS = [
  ['', 'custom'],
  ['*/15 * * * *', 'every 15 minutes'],
  ['0 * * * *', 'hourly'],
  ['0 9 * * 1-5', 'weekdays 09:00'],
  ['0 2 * * *', 'daily 02:00'],
  ['0 6 * * 1', 'Mondays 06:00'],
];
const TZ = typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC';
const rel = (t) => {
  const d = t - Date.now();
  if (d < 0) return 'due';
  if (d < 90e3) return 'in a minute';
  if (d < 3600e3) return `in ${Math.round(d / 60e3)} min`;
  if (d < 86400e3) return `in ${(d / 3600e3).toFixed(1)} h`;
  return `in ${Math.round(d / 86400e3)} d`;
};

export default function SchedulePanel({ pipeline, onChange, orgId, workspaceId, canEdit = true, hasCallInput, uid }) {
  const s = pipeline?.schedule || {};
  const st = pipeline?.scheduleState || {};
  const [cron, setCron] = useState(s.cron || '');
  const [tz, setTz] = useState(s.tz || TZ);
  const dirty = cron !== (s.cron || '') || tz !== (s.tz || TZ);
  const save = (patch) => onChange({ schedule: { ...s, cron, tz, by: uid || s.by || null, ...patch } });
  const triggers = [
    s.enabled && s.cron ? `cron ${s.cron} (${s.tz || 'UTC'})` : null,
    pipeline?.timers?.enabled ? `block timers on ${Object.values(pipeline.timers.blocks || {}).map((b) => `${b.label} (every ${b.everyMinutes} min)`).join(', ')} — each re-runs its block and downstream` : null,
    hasCallInput ? 'called by another pipeline or the run_pipeline tool' : null,
    'Run button / NoVA run_pipeline',
    pipeline?.mode === 'streaming' ? 'streaming: always on while started' : null,
  ].filter(Boolean);

  return (
    <div data-nova-note={`Schedule card: ${s.enabled && s.cron ? `enabled, cron ${s.cron} in ${s.tz || 'UTC'}${st.nextAt ? `, next ${rel(st.nextAt)}` : ''}` : 'no schedule'}. ${st.firedCount || 0} scheduled run(s) so far.`}>
      <label className="cs-help" style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
        <input type="checkbox" disabled={!canEdit || !cron} checked={!!s.enabled} onChange={(e) => save({ enabled: e.target.checked })} />
        {s.enabled ? 'Auto-run on schedule' : 'Run on a schedule'}
      </label>
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        <select className="cs-input" style={{ padding: '2px 6px', fontSize: 12 }} value={PRESETS.some(([v]) => v === cron) ? cron : ''} onChange={(e) => { if (e.target.value) setCron(e.target.value); }} aria-label="schedule preset" disabled={!canEdit}>
          {PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input className="cs-input" style={{ flex: 1, minWidth: 120, padding: '2px 6px', fontSize: 12, fontFamily: 'ui-monospace, monospace' }} placeholder="m h dom mon dow" value={cron} onChange={(e) => setCron(e.target.value)} aria-label="cron expression" disabled={!canEdit} />
        <input className="cs-input" style={{ width: 150, padding: '2px 6px', fontSize: 12 }} value={tz} onChange={(e) => setTz(e.target.value)} aria-label="time zone" title="IANA time zone" disabled={!canEdit} />
        {dirty && canEdit && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => save({})}>Save schedule</button>}
      </div>
      <label className="cs-help" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: 12 }}>
        <input type="checkbox" disabled={!canEdit} checked={s.skipIfRunning !== false} onChange={(e) => save({ skipIfRunning: e.target.checked })} />
        skip a tick while the previous scheduled run is still going
      </label>
      {st.lastError && <p className="cs-help" style={{ fontSize: 12, marginTop: 6, color: 'var(--danger, #e5484d)' }}>{st.lastError}</p>}
      <p className="cs-help" style={{ fontSize: 12, marginTop: 8 }}>
        {s.enabled && st.nextAt ? <>Next run <strong>{rel(st.nextAt)}</strong> ({new Date(st.nextAt).toLocaleString()}). </> : s.enabled ? 'Arming… ' : ''}
        {st.lastFiredAt ? <>Last fired {new Date(st.lastFiredAt).toLocaleString()}{st.lastJobId ? <> · <Link to={`/orgs/${orgId}/ws/${workspaceId}/runs/${st.lastJobId}`}>run</Link></> : ''}. </> : ''}
        {st.firedCount ? `${st.firedCount} scheduled run${st.firedCount === 1 ? '' : 's'}${st.skippedCount ? `, ${st.skippedCount} skipped` : ''}.` : ''}
      </p>
      <p className="cs-help" style={{ fontSize: 11.5, marginTop: 6 }}>Triggers: {triggers.join(' · ')}.</p>
    </div>
  );
}
